import { useRef, useState, useEffect } from "react";
import uploadImg from "../../../assets/images/uploadImg.svg";
import FormInput from "../../../shared/FormInputs";
import Button from "../../../shared/Button";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEmployees } from "../modal/EmployeesContext";
import { useNavigate } from "react-router-dom";
import SuccessfullModal from "../../../shared/SuccessfullModal";
import DropdownTrigger from "../../../shared/DropdownTrigger";
import selectArrow from "../../../assets/images/selectBoxArrow.svg"
import { createEmployee, updateEmployee as updateEmployeeApi, uploadProfilePic } from "../api/employeesApi";



const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}){1,2}$/;
const hexColorField = () => Yup.string().matches(HEX_COLOR_REGEX, { message: "Must be a valid hex color, e.g. #3B82F6", excludeEmptyString: true });

const createFormSchema = Yup.object().shape({
    employeeCode: Yup.string().required("Employee Code is required"),
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Password is required"),
    cnic: Yup.string().required("CNIC is required").matches(/^\d{5}-\d{7}-\d$/, "CNIC must be in format XXXXX-XXXXXXX-X"),
    designation: Yup.string().required("Designation is required"),
    department: Yup.string().required("Department is required"),
    dateOfBirth: Yup.string().required("Date of birth is required"),
    date: Yup.string().required("Joining date is required"),
    homeAddress: Yup.string().required("Home address is required"),
    bankName: Yup.string().required("Bank name is required"),
    bankTitle: Yup.string().required("Bank title is required"),
    bankAccountNumber: Yup.string().required("Account number is required"),
    bankIBAN: Yup.string().required("IBAN is required"),
    bankBranchCode: Yup.string().required("Branch code is required"),
    initialBaseSalary: Yup.number().required("Initial salary is required").min(1, "Salary must be greater than 0"),
    primaryColor: hexColorField(),
    secondaryColor: hexColorField(),
    accentColor: hexColorField(),
    backgroundColor: hexColorField(),
});

const editFormSchema = Yup.object().shape({
    employeeCode: Yup.string().required("Employee Code is required"),
    name: Yup.string().required("Name is required"),
    cnic: Yup.string().required("CNIC is required").matches(/^\d{5}-\d{7}-\d$/, "CNIC must be in format XXXXX-XXXXXXX-X"),
    designation: Yup.string().required("Designation is required"),
    department: Yup.string().required("Department is required"),
    dateOfBirth: Yup.string().required("Date of birth is required"),
    date: Yup.string().required("Joining date is required"),
    homeAddress: Yup.string().required("Home address is required"),
    bankName: Yup.string().required("Bank name is required"),
    bankTitle: Yup.string().required("Bank title is required"),
    bankAccountNumber: Yup.string().required("Account number is required"),
    bankIBAN: Yup.string().required("IBAN is required"),
    bankBranchCode: Yup.string().required("Branch code is required"),
    initialBaseSalary: Yup.number().required("Initial salary is required").min(1, "Salary must be greater than 0"),
    primaryColor: hexColorField(),
    secondaryColor: hexColorField(),
    accentColor: hexColorField(),
    backgroundColor: hexColorField(),
});


const Form = () => {
    const [file, setFile] = useState<string | null>(null);
    const [fileObj, setFileObj] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { clearError, idExistError, successfullModal, setSuccessfullModal, setEditingEmployee, editingEmployee } = useEmployees();
    const [apiError, setApiError] = useState("");
    const isEditMode = editingEmployee !== null;
    const navigate = useNavigate();
    const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
    const [departmentsDropdownOpen, setDepartmentsDropdownOpen] = useState(false)
    const [bankName, setBankName] = useState(editingEmployee?.bankName || "Select the bank")
    const [departmentName, setDepartmentName] = useState(editingEmployee?.department || "Select the Department")

    const banksOptions = ["Meezan", "UBL", "Allied", "HBL"];
    const departmentsOptions = ["Engineering", "HR", "Marketing", "Office Maintenance"]

    const departmentModalRef = useRef<HTMLDivElement>(null);
    const bankNameModalRef = useRef<HTMLDivElement>(null);

    const openBanksDropdown = () => {
        setBankDropdownOpen(!bankDropdownOpen);
    };
    const openDepartmentDropdown = () => {
        setDepartmentsDropdownOpen(!departmentsDropdownOpen);
    };
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                bankNameModalRef.current &&
                !bankNameModalRef.current.contains(event.target as Node)
            ) {
                setBankDropdownOpen(false);
            }
            if (
                departmentModalRef.current &&
                !departmentModalRef.current.contains(event.target as Node)
            ) {
                setDepartmentsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const addBankName = (item: string) => {
        setBankName(item)
        setBankDropdownOpen(false)
        formik.setFieldValue('bankName', item)
    }

    const addDepartmentName = (item: string) => {
        setDepartmentName(item)
        setDepartmentsDropdownOpen(false)
        formik.setFieldValue('department', item)
    }

    const labelStyles = "block text-sm font-medium text-foreground mb-1 font-inter"
    const inputBorder = "mt-1.5 w-full"
    const inputStyles = "text-sm leading-normal px-4 py-3 bg-surface-muted border border-border rounded-xl text-foreground font-inter placeholder-muted-foreground focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
    const errorClasses = "text-destructive text-xs mt-1 absolute -bottom-5 font-inter"

    const handleClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(URL.createObjectURL(selectedFile));
            setFileObj(selectedFile);
        }
    };

    const handleSubmit = async (values: any) => {
        setApiError("");

        try {
            // Upload profile pic first if selected
            let profilePicUrl = null;
            if (fileObj) {
                profilePicUrl = await uploadProfilePic(fileObj);
            }

            if (editingEmployee !== null) {
                await updateEmployeeApi(
                    editingEmployee.id || "",
                    { ...values, profilePicUrl: profilePicUrl || editingEmployee.image }
                );
            } else {
                await createEmployee({ ...values, profilePicUrl });
            }
            formik.resetForm();
            setFile(null);
            setFileObj(null);
            setSuccessfullModal(true);
        } catch (err: any) {
            const detail = err.response?.data?.detail || "Failed to save employee. Please try again.";
            setApiError(detail);
        }
    }


    const successfullyAdded = () => {
        setEditingEmployee(null)
        setSuccessfullModal(false)
        navigate('/admin/employees')
        window.scrollTo(0, 0);
        document.body.style.overflow = "auto"
    }

    const latestIncreament = editingEmployee?.lastIncreament?.reduce((latest, current) => {
        const latestDate = new Date(latest.increamentDate)
        const currentDate = new Date(current.increamentDate)

        return currentDate > latestDate ? current : latest;
    })

    const formik = useFormik({
        initialValues: {
            employeeCode: editingEmployee?.id || "",
            name: editingEmployee?.name || "",
            email: editingEmployee?.email || "",
            secondaryEmail: editingEmployee?.secondaryEmail || "",
            password: "",  // Never pre-fill password on edit
            cnic: editingEmployee?.cnic || "",
            designation: editingEmployee?.designation || "",
            department: editingEmployee?.department || "",
            hobbies: editingEmployee?.hobbies || "",
            vehicleRegistrationNumber: editingEmployee?.vehicleRegistrationNumber || "",
            primaryColor: editingEmployee?.primaryColor || "",
            secondaryColor: editingEmployee?.secondaryColor || "",
            accentColor: editingEmployee?.accentColor || "",
            backgroundColor: editingEmployee?.backgroundColor || "",
            dateOfBirth: editingEmployee?.dateOfBirth || "",
            actualDateOfBirth: editingEmployee?.actualDateOfBirth || "",
            date: editingEmployee?.date || "",
            fullTimeJoinDate: editingEmployee?.fullTimeJoinDate || "",
            lastIncreamentDate: editingEmployee ? (latestIncreament?.increamentDate) : "",
            homeAddress: editingEmployee?.homeAddress || "",
            bankName: editingEmployee?.bankName || "",
            bankTitle: editingEmployee?.bankTitle || "",
            bankAccountNumber: editingEmployee?.bankAccountNumber || "",
            bankIBAN: editingEmployee?.bankIBAN || "",
            bankBranchCode: editingEmployee?.bankBranchCode || "",
            initialBaseSalary: editingEmployee?.initialBaseSalary || "",
            currentBaseSalary: editingEmployee ? (editingEmployee.currentBaseSalary || editingEmployee.initialBaseSalary) : "",
            increamentAmount: editingEmployee ? (latestIncreament?.increamentAmount) : 0,
        },
        validationSchema: editingEmployee ? editFormSchema : createFormSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    });
    useEffect(() => {
        if (editingEmployee) {
            setBankName(editingEmployee.bankName || "Select the bank")
            setDepartmentName(editingEmployee.department || "Select the Department")
        }
    }, [editingEmployee])




    return (
        <>
            <form onSubmit={formik.handleSubmit} noValidate className="mt-6 space-y-8">

                {/* Email display (edit mode only — not editable) */}
                {isEditMode && editingEmployee?.email && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-surface-muted/50 border border-border rounded-xl">
                        <span className="text-xs text-muted-foreground font-inter">Login email</span>
                        <span className="text-sm text-foreground font-inter">{editingEmployee.email}</span>
                    </div>
                )}

                {/* Secondary email — editable, used for job alert matching */}
                {isEditMode && (
                    <div className="relative">
                        <FormInput
                            label="Secondary Email"
                            name="secondaryEmail"
                            type="email"
                            value={formik.values.secondaryEmail}
                            onChange={formik.handleChange}
                            placeholder="Personal or alternate email"
                            labelClassName={labelStyles}
                            inputMainBorder={inputBorder}
                            inputClassName={inputStyles}
                        />
                        <p className="text-xs text-muted-foreground font-inter mt-1">Used to match job alerts — not for login.</p>
                    </div>
                )}

                {/* Photo Upload — compact inline */}
                <div className="flex items-center gap-4">
                    <Button disableDefaultStyles type="button" onClick={handleClick} buttonClasses="flex-shrink-0 w-16 h-16 border border-dashed border-border hover:border-border bg-surface-muted/50 rounded-full flex items-center justify-center transition-colors overflow-hidden">
                        {file ? (
                            <img src={file} alt="Uploaded preview" className="w-full h-full object-cover" />
                        ) : (
                            <img src={uploadImg} alt="profile" className="w-8 h-8 opacity-50" />
                        )}
                        <input type="file" accept="image/*" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    </Button>
                    <div>
                        <p className="text-sm text-foreground font-inter">Profile Photo</p>
                        <p className="text-xs text-muted-foreground font-inter">JPG, PNG or GIF. Max 10MB</p>
                    </div>
                </div>

                {/* Personal Info */}
                <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4 font-inter">Personal Information</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="relative">
                        <FormInput id="name"
                            name="name"
                            placeholder="Name"
                            onChange={formik.handleChange}
                            value={formik.values.name} label="Name" labelClassName={`${labelStyles}`} type="text" inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />

                        {formik.errors.name && formik.touched.name && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.name}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Employee Code"
                            type="text"
                            id="employeeCode"
                            name="employeeCode"
                            value={formik.values.employeeCode}
                            onChange={(e) => {
                                formik.handleChange(e);
                                clearError();
                            }}
                            placeholder="e.g. EMP-001" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.employeeCode && formik.touched.employeeCode && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.employeeCode}
                            </p>
                        )}
                        {idExistError && (
                            <p className={`${errorClasses}`}>
                                {idExistError}
                            </p>
                        )}
                    </div>
                    {!editingEmployee && (
                    <>
                    <div className="relative">
                        <FormInput label="Email" name="email" type="email" value={formik.values.email} onChange={formik.handleChange} placeholder="Email" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.email && formik.touched.email && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.email}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Password" name="password" type="password" value={formik.values.password} onChange={formik.handleChange} placeholder="Password" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.password && formik.touched.password && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.password}
                            </p>
                        )}
                    </div>
                    </>
                    )}
                    <div className="relative">
                        <FormInput label="CNIC" name="cnic" value={formik.values.cnic} onChange={formik.handleChange} placeholder="12345-6789012-3" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.cnic && formik.touched.cnic && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.cnic}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Designation" name="designation" value={formik.values.designation} onChange={formik.handleChange} placeholder="CEO" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.designation && formik.touched.designation && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.designation}
                            </p>
                        )}
                    </div>
                </div>
                </div>

                {/* Work Details */}
                <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4 font-inter">Work Details</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="relative" ref={departmentModalRef}>
                        <label className={`${labelStyles}`}>Department</label>
                        <div className={`${inputBorder}`}>
                            <DropdownTrigger
                                onClick={openDepartmentDropdown}
                                selectClassName={`${inputStyles} ${departmentName === "Select the Department" ? "!text-muted-foreground" : "!text-foreground"} cursor-pointer w-full justify-between`}
                                children={departmentName}
                                selectArrowClassName={`${departmentsDropdownOpen ? "-rotate-[180deg]" : "rotate-0"
                                    } transition-all`}
                                selectArrowPath={selectArrow}
                            />
                        </div>
                        {departmentsDropdownOpen && (
                            <div className="bodyBackground absolute top-[110px] md:top-[133px] rounded-[15px] overflow-hidden shadow-xl right-0 w-full z-[9999]">
                                <ul>
                                    {departmentsOptions.map((item, index) => (
                                        <li key={index} className="w-full">
                                            <Button
                                                type="button"
                                                onClick={() => addDepartmentName(item)}
                                                buttonClasses="border-b border-solid border-border px-5 py-2.5 text-foreground text-sm w-full text-left hover:opacity-[0.4] transition-all"
                                            >
                                                {item}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {formik.errors.department && formik.touched.department && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.department}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Hobbies" name="hobbies" value={formik.values.hobbies} onChange={formik.handleChange} placeholder="Football" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.hobbies && formik.touched.hobbies && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.hobbies}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Vehicle Registration Number" name="vehicleRegistrationNumber" value={formik.values.vehicleRegistrationNumber} onChange={formik.handleChange} placeholder="ABC-123" labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.vehicleRegistrationNumber && formik.touched.vehicleRegistrationNumber && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.vehicleRegistrationNumber}
                            </p>
                        )}
                    </div>
                </div>
                </div>

                {/* Appearance — optional color scheme used to auto-fill {{color_scheme}} in Prompt Library */}
                <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4 font-inter">Appearance</h3>
                    <p className="text-xs text-muted-foreground font-inter mb-4">Optional. Used to auto-fill the color_scheme placeholder in Prompt Library. Leave any field blank to omit it.</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {([
                        { name: "primaryColor", label: "Primary Color" },
                        { name: "secondaryColor", label: "Secondary Color" },
                        { name: "accentColor", label: "Accent Color" },
                        { name: "backgroundColor", label: "Background Color" },
                    ] as const).map(({ name, label }) => (
                        <div className="relative" key={name}>
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <FormInput
                                        label={label}
                                        name={name}
                                        value={formik.values[name]}
                                        onChange={formik.handleChange}
                                        placeholder="#3B82F6"
                                        labelClassName={`${labelStyles}`}
                                        inputMainBorder={`${inputBorder}`}
                                        inputClassName={`${inputStyles}`}
                                    />
                                </div>
                                <span
                                    className="w-9 h-9 mt-1.5 rounded-lg border border-border flex-shrink-0"
                                    style={{ backgroundColor: HEX_COLOR_REGEX.test(formik.values[name] || "") ? formik.values[name] : undefined }}
                                    aria-hidden="true"
                                />
                            </div>
                            {formik.errors[name] && formik.touched[name] && (
                                <p className={`${errorClasses}`}>
                                    {formik.errors[name]}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
                </div>

                {/* Dates */}
                <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4 font-inter">Dates</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="relative">
                        <FormInput label="Date of Birth" name="dateOfBirth" type="date" placeholder="mm/dd/yyyy" value={formik.values.dateOfBirth} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles} ${formik.values.dateOfBirth === "" ? "!text-muted-foreground" : "!text-foreground"}`} />
                        {formik.errors.dateOfBirth && formik.touched.dateOfBirth && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.dateOfBirth}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Actual Date of Birth" name="actualDateOfBirth" type="date" placeholder="mm/dd/yyyy" value={formik.values.actualDateOfBirth} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles} ${formik.values.actualDateOfBirth === "" ? "!text-muted-foreground" : "!text-foreground"}`} />
                        {formik.errors.actualDateOfBirth && formik.touched.actualDateOfBirth && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.actualDateOfBirth}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput
                            label="Joining Date"
                            name="date"
                            type="date"
                            placeholder="mm/dd/yyyy"
                            value={formik.values.date}
                            onChange={formik.handleChange}
                            labelClassName={labelStyles}
                            inputMainBorder={inputBorder}
                            inputClassName={`${inputStyles} ${formik.values.date === "" ? "!text-muted-foreground" : "!text-foreground"}`}
                        />

                        {formik.errors.date && formik.touched.date && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.date}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Full Joining Date" name="fullTimeJoinDate" type="date" placeholder="mm/dd/yyyy" value={formik.values.fullTimeJoinDate} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles} ${formik.values.fullTimeJoinDate === "" ? "!text-muted-foreground" : "!text-foreground"}`} />
                    </div>
                    <div className="relative">
                        <FormInput label="Last Increment Date" name="lastIncreamentDate" type="date" placeholder="mm/dd/yyyy" value={formik.values.lastIncreamentDate} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles} ${formik.values.lastIncreamentDate === "" ? "!text-muted-foreground" : "!text-foreground"}`} />
                    </div>
                </div>
                </div>

                {/* Salary & Banking */}
                <div>
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4 font-inter">Salary & Banking</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="relative">
                        <FormInput type="number" label="Initial Base Salary" name="initialBaseSalary" value={formik.values.initialBaseSalary} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="50000" inputClassName={`${inputStyles}`} />
                        {formik.errors.initialBaseSalary && formik.touched.initialBaseSalary && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.initialBaseSalary}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput type="number" label="Current Base Salary" name="currentBaseSalary" value={formik.values.currentBaseSalary} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="50000" inputClassName={`${inputStyles}`} />
                        {formik.errors.currentBaseSalary && formik.touched.currentBaseSalary && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.currentBaseSalary}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Increment Amount" name="increamentAmount" type="number" value={formik.values.increamentAmount} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} inputClassName={`${inputStyles}`} />
                        {formik.errors.increamentAmount && formik.touched.increamentAmount && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.increamentAmount}
                            </p>
                        )}
                    </div>
                    <div className="relative" ref={bankNameModalRef}>
                        <label className={`${labelStyles}`}>Bank Name</label>
                        <div className={`${inputBorder}`}>
                            <DropdownTrigger
                                onClick={openBanksDropdown}
                                selectClassName={`${inputStyles} ${bankName === "Select the bank" ? "!text-muted-foreground" : "!text-foreground"} cursor-pointer w-full justify-between`}
                                children={bankName}
                                selectArrowClassName={`${bankDropdownOpen ? "-rotate-[180deg]" : "rotate-0"
                                    } transition-all`}
                                selectArrowPath={selectArrow}
                            />
                        </div>
                        {bankDropdownOpen && (
                            <div className="bodyBackground absolute top-[110px] md:top-[133px] rounded-[15px] overflow-hidden shadow-xl right-0 w-full z-[9999]">
                                <ul>
                                    {banksOptions.map((item, index) => (
                                        <li key={index} className="w-full">
                                            <Button
                                                type="button"
                                                onClick={() => addBankName(item)}
                                                buttonClasses="border-b border-solid border-border px-5 py-2.5 text-foreground text-sm w-full text-left hover:opacity-[0.4] transition-all"
                                            >
                                                {item}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {formik.errors.bankName && formik.touched.bankName && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.bankName}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput type="text" label="Bank Title" name="bankTitle" value={formik.values.bankTitle} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="Bank Ltd" inputClassName={`${inputStyles}`} />
                        {formik.errors.bankTitle && formik.touched.bankTitle && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.bankTitle}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput type="text" label="Bank Account Number" name="bankAccountNumber" value={formik.values.bankAccountNumber} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="12345678" inputClassName={`${inputStyles}`} />
                        {formik.errors.bankAccountNumber && formik.touched.bankAccountNumber && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.bankAccountNumber}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Bank IBAN" name="bankIBAN" value={formik.values.bankIBAN} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="PK36SCBL0000001123456702" inputClassName={`${inputStyles}`} />
                        {formik.errors.bankIBAN && formik.touched.bankIBAN && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.bankIBAN}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Bank Branch Code" name="bankBranchCode" value={formik.values.bankBranchCode} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="0949" inputClassName={`${inputStyles}`} />
                        {formik.errors.bankBranchCode && formik.touched.bankBranchCode && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.bankBranchCode}
                            </p>
                        )}
                    </div>
                    <div className="relative">
                        <FormInput label="Home Address" name="homeAddress" value={formik.values.homeAddress} onChange={formik.handleChange} labelClassName={`${labelStyles}`} inputMainBorder={`${inputBorder}`} placeholder="House No.. Street No.." inputClassName={`${inputStyles}`} />
                        {formik.errors.homeAddress && formik.touched.homeAddress && (
                            <p className={`${errorClasses}`}>
                                {formik.errors.homeAddress}
                            </p>
                        )}
                    </div>
                </div>
                </div>


                {apiError && (
                    <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                        <p className="text-destructive text-sm font-inter">{apiError}</p>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={editingEmployee ? !formik.dirty : formik.isSubmitting}
                    className={`mt-6 px-8 py-3 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors`}
                >
                    {formik.isSubmitting ? 'Saving...' : editingEmployee ? 'Update Employee' : 'Register Employee'}
                </button>
            </form>
            {successfullModal &&
                <SuccessfullModal modalClassName="" modalMain="" successfullOk={successfullyAdded}>
                    {isEditMode ? 'Successfully Updated your Employee.' : 'Successfully Registered your Employee.'}
                </SuccessfullModal>
            }
        </>
    )
}

export default Form
